import dayjs from 'dayjs'
import { Controller } from '@hotwired/stimulus'

import { LocalClient } from '../clients'
import { targetUpdate } from './components/component_controller'
import { dispatcher } from './pub_controller'
import { Location, SelectOptionSingle } from '../types/'

interface OptionsFromApi {
  options: SelectOptionSingle[]
}

interface Organization {
  erp_id: string
  id: string
  name: string
  url: string
}

export class UsageEventFormController extends Controller {
  static targets = ['device', 'incidentDate', 'location', 'site']
  static values = { organizations: Array }

  declare readonly deviceTarget: HTMLElement
  declare readonly incidentDateTarget: HTMLElement
  declare readonly locationTarget: HTMLElement
  declare readonly siteTarget: HTMLElement

  declare readonly organizationsValue: Organization[]

  deviceId = '' as String
  locationId = '' as String
  organizationId = '' as String
  siteId = '' as String

  async handleOrganizationSelect(event) {
    const organizationId = event.detail.value
    if (organizationId !== this.organizationId) {
      this.organizationId = organizationId
      this.locationId = ''

      const organization = this.organizationsValue.find(
        ({ id }) => id === organizationId,
      )
      if (organization) {
        dispatcher(this, 'organization', 'usage_event', organization)
      }

      const [locations, devices] = await Promise.all([
        this.getOrganizationLocations(organizationId),
        this.getOrganizationDevices(organizationId),
      ])
      const selectedLocation =
        locations.options.length === 1 ? locations.options[0].value : null
      targetUpdate(this.locationTarget, {
        disabled: false,
        options: locations.options,
        value: selectedLocation,
      })
      this.updateMaxDate(selectedLocation)
      targetUpdate(this.siteTarget, {
        disabled: true,
        options: [],
        value: null,
      })
      const selectedDevice =
        devices.options.length === 1 ? devices.options[0].value : null
      targetUpdate(this.deviceTarget, {
        disabled: false,
        options: devices.options,
        value: selectedDevice,
      })
    }
  }

  async handleLocationSelect(event) {
    const locationId = event.detail.value
    if (locationId !== this.locationId) {
      this.locationId = locationId || ''
      this.updateMaxDate(locationId)
      const [sites, devices] = await Promise.all([
        this.getLocationSites(locationId),
        this.getLocationDevices(locationId),
      ])
      const selectedSite =
        sites.options.length === 1 ? sites.options[0].value : null
      targetUpdate(this.siteTarget, {
        disabled: false,
        options: sites.options,
        value: selectedSite,
      })
      const selectedDevice =
        devices.options.length === 1 ? devices.options[0].value : null
      targetUpdate(this.deviceTarget, {
        disabled: false,
        options: devices.options,
        value: selectedDevice,
      })
    }
  }

  async handleSiteSelect(event) {
    const siteId = event.detail.value
    if (siteId !== this.siteId) {
      this.siteId = siteId
      const devices = await this.getSiteDevices(siteId)
      const selectedDevice =
        devices.options.length === 1 ? devices.options[0].value : null
      targetUpdate(this.deviceTarget, {
        disabled: false,
        options: devices.options,
        value: selectedDevice,
      })
    }
  }

  async handleDeviceSelect(event) {
    const deviceId = event.detail.value
    if (deviceId !== this.deviceId) {
      this.deviceId = deviceId
      if (!deviceId) return
      const device = await this.getDevice(deviceId)
      const sites = await this.getLocationSites(device.location_id)
      this.locationId = device.location_id || ''
      this.updateMaxDate(device.location_id)
      targetUpdate(this.locationTarget, {
        value: device.location_id,
      })
      this.siteId = device.site_id || ''
      targetUpdate(this.siteTarget, {
        disabled: false,
        options: sites.options,
        value: device.site_id,
      })
    }
  }

  getOrganizationDevices(organizationId): Promise<OptionsFromApi> {
    if (!organizationId) return Promise.resolve({ options: [] })

    const path = '/organization_devices'
    const params = `organization_id=${organizationId}`
    return LocalClient.queryPath({ path, params })
  }

  getOrganizationLocations(organizationId): Promise<OptionsFromApi> {
    if (!organizationId) return Promise.resolve({ options: [] })

    const path = '/organization_locations'
    const params = `organization_id=${organizationId}`
    return LocalClient.queryPath({ path, params })
  }

  getLocation(locationId): Promise<Location> {
    if (!locationId) return Promise.resolve(null)

    return LocalClient.fetchLocation(locationId)
  }

  getLocationDevices(locationId): Promise<OptionsFromApi> {
    if (!locationId) return Promise.resolve({ options: [] })

    const path = `/location_devices`
    const params = `location_id=${locationId}`
    return LocalClient.queryPath({ path, params })
  }

  getLocationSites(locationId): Promise<OptionsFromApi> {
    if (!locationId) return Promise.resolve({ options: [] })

    const path = `/locations/sites`
    const params = `location_ids=${locationId}`
    return LocalClient.queryPath({ path, params })
  }

  getSiteDevices(siteId): Promise<OptionsFromApi> {
    if (!siteId) return Promise.resolve({ options: [] })

    const path = `/site_devices`
    const params = `site_id=${siteId}`
    return LocalClient.queryPath({ path, params })
  }

  getDevice(deviceId): Promise<{ location_id: String; site_id: String }> {
    return fetch(`/devices/${deviceId}.json`)
      .then((response) => response.json())
      .then(({ device }) => device)
  }

  async updateMaxDate(locationId) {
    const location = await this.getLocation(locationId)
    const timeZone = location?.physical_address?.time_zone
    let date
    if (timeZone) {
      date = dayjs.tz(new Date(), timeZone)
    }
    targetUpdate(this.incidentDateTarget, {
      maxDate: date,
    })
  }
}
